/**
 * Daily View Component
 * Shows detailed breakdown of hours by project for a single day
 */

import React from 'react';
import { Card, Table, Tag, Space, Tooltip, Empty } from 'antd';
import { useDailySummaries } from '@/hooks/useTimesheet';
import type { DailySummary } from '@/api/types/timesheet.types';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import './DailyView.css';

interface DailyViewProps {
  date: string; // YYYY-MM-DD
  showAll: boolean; // Show all statuses or only approved
  canEdit: boolean;
}

export const DailyView: React.FC<DailyViewProps> = ({
  date,
  showAll,
  canEdit: _canEdit,
}) => {
  const { data, isLoading } = useDailySummaries({
    date_from: date,
    date_to: date,
  });

  const summaries = data?.items || [];

  // Calculate totals
  const totals = summaries.reduce(
    (acc, summary) => {
      const hours = showAll
        ? summary.total_hours_logged
        : summary.approved_hours_logged;
      const billable = summary.billable_hours_logged;
      const nonBillable = summary.non_billable_hours_logged;

      return {
        hours: acc.hours + hours,
        billable: acc.billable + billable,
        nonBillable: acc.nonBillable + nonBillable,
      };
    },
    { hours: 0, billable: 0, nonBillable: 0 }
  );

  const columns: ColumnsType<DailySummary> = [
    {
      title: 'Project',
      dataIndex: 'project_id',
      key: 'project_id',
      render: (projectId) => (
        <span style={{ fontWeight: 500 }}>{projectId}</span>
      ),
    },
    {
      title: 'Entries',
      dataIndex: 'number_of_entries',
      key: 'entries',
      width: 100,
      align: 'center',
      render: (count) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Total Hours',
      dataIndex: showAll ? 'total_hours_logged' : 'approved_hours_logged',
      key: 'hours',
      width: 120,
      align: 'right',
      render: (hours) => (
        <span className="timesheet-daily-hours">{hours.toFixed(2)}h</span>
      ),
    },
    {
      title: 'Billable',
      dataIndex: 'billable_hours_logged',
      key: 'billable',
      width: 120,
      align: 'right',
      render: (hours) => (
        <span style={{ color: 'var(--timesheet-billable)' }}>
          {hours.toFixed(2)}h
        </span>
      ),
    },
    {
      title: 'Non-Billable',
      dataIndex: 'non_billable_hours_logged',
      key: 'nonBillable',
      width: 120,
      align: 'right',
      render: (hours) => (
        <span style={{ color: 'var(--timesheet-non-billable)' }}>
          {hours.toFixed(2)}h
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 200,
      render: (_, record) => (
        <Space size={4}>
          {record.approved_hours_logged > 0 && (
            <Tooltip
              title={`${record.approved_hours_logged.toFixed(2)}h approved`}
            >
              <Tag color="blue">
                Approved: {record.approved_hours_logged.toFixed(1)}h
              </Tag>
            </Tooltip>
          )}
          {record.pending_hours_logged > 0 && (
            <Tooltip
              title={`${record.pending_hours_logged.toFixed(2)}h pending`}
            >
              <Tag color="orange">
                Pending: {record.pending_hours_logged.toFixed(1)}h
              </Tag>
            </Tooltip>
          )}
          {record.rejected_hours_logged > 0 && (
            <Tooltip
              title={`${record.rejected_hours_logged.toFixed(2)}h rejected`}
            >
              <Tag color="red">
                Rejected: {record.rejected_hours_logged.toFixed(1)}h
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="timesheet-daily-view">
      {/* Summary Cards */}
      <div className="timesheet-daily-summary">
        <Card className="timesheet-daily-card">
          <div className="timesheet-daily-card-title">Total Hours</div>
          <div className="timesheet-daily-card-value">
            {totals.hours.toFixed(2)}h
          </div>
        </Card>
        <Card className="timesheet-daily-card">
          <div className="timesheet-daily-card-title">Billable</div>
          <div
            className="timesheet-daily-card-value"
            style={{ color: 'var(--timesheet-billable)' }}
          >
            {totals.billable.toFixed(2)}h
          </div>
        </Card>
        <Card className="timesheet-daily-card">
          <div className="timesheet-daily-card-title">Non-Billable</div>
          <div
            className="timesheet-daily-card-value"
            style={{ color: 'var(--timesheet-non-billable)' }}
          >
            {totals.nonBillable.toFixed(2)}h
          </div>
        </Card>
        <Card className="timesheet-daily-card">
          <div className="timesheet-daily-card-title">Date</div>
          <div className="timesheet-daily-card-value">
            {dayjs(date).format('MMM D, YYYY')}
          </div>
        </Card>
      </div>

      {/* Projects Table */}
      <Card className="timesheet-daily-table-card">
        <Table
          columns={columns}
          dataSource={summaries}
          loading={isLoading}
          rowKey={(record) => `${record.project_id}-${record.summary_date}`}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description="No timesheet entries for this day"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};
